import React, { useEffect, useState } from 'react';
import { getEvaluations, setEvaluationStatus, applyEvaluation, applyEvaluationConfig, dismissAllEvaluations, validateEvaluation, scoreEvaluation, getEvaluationScore, runEvaluationsNow } from '../../../services/StrategiesService';
import { getSettings, updateSettings } from '../../../services/SettingsService';
import { processError } from '../../../services/ServiceUtils';
import { getCurrentMarket, subscribeToCurrentMarket } from '../../../utils/MarketStorage';
import { confirmDialog } from '../../../utils/ConfirmDialog';

const IMPACT_COLOR = { high: 'danger', medium: 'warning', low: 'secondary' };

// Sugestão de `conditions` precisa ser uma expressão &&-delimitada com MEMORY[] — senão não
// é aplicável automaticamente (mesma regra do backend applyEvaluation / ExportStrategyModal).
function isConditionsInvalid(s) {
    if (s.field !== 'conditions' || s.suggestedValue == null) return false;
    return typeof s.suggestedValue !== 'string' || !s.suggestedValue.includes('MEMORY[');
}
function isApplicable(s) {
    return s.field !== 'description_only' && s.suggestedValue != null && !isConditionsInvalid(s);
}

// Selo da Camada 1 (Consistente/Inconsistente) — resultado da validação no simulador.
function ValidationBadge({ v }) {
    if (!v || v.loading) return null;
    if (v.error) return <span className="badge bg-secondary" title={v.error}>erro ao validar</span>;
    if (v.verdict === 'na') return <span className="badge bg-light text-dark border" title={v.reason || 'Nenhuma sugestão simulável.'}>nada a simular</span>;
    if (v.verdict === 'consistent')
        return <span className="badge bg-success" title="As mudanças disparam corretamente e não introduzem anomalias no simulador.">✓ Consistente</span>;
    const reasons = [v.reason, ...(v.newAnomalies || []), ...(v.newErrors || []),
        ...((v.deadAutomations || []).map(d => `automação ficaria inerte: ${d}`))].filter(Boolean).join(' · ');
    return <span className="badge bg-danger" title={reasons || 'A mudança quebra o comportamento no simulador.'}>✗ Inconsistente</span>;
}

// Selo da Camada 2 (Aprovada/Reprovada) — resultado do backtest A/B em candles reais.
function ScoreBadge({ s }) {
    if (!s || s.loading) return null;
    if (s.error) return <span className="badge bg-secondary" title={s.error}>erro no backtest</span>;
    if (s.verdict === 'na') return <span className="badge bg-light text-dark border" title={s.reason || 'Nenhuma sugestão backtest-ável.'}>nada a testar</span>;
    const o = s.original || {}, m = s.modified || {};
    const tip = `Backtest ${s.days}d em ${s.symbol}\nLucro: ${o.profitPerc}% → ${m.profitPerc}% (Δ ${s.deltaPerc >= 0 ? '+' : ''}${s.deltaPerc}pp)\nDrawdown: ${o.maxDrawdown}% → ${m.maxDrawdown}%\nWin rate: ${o.winRate}% → ${m.winRate}%`;
    return <span className={`badge bg-${s.verdict === 'approved' ? 'success' : 'danger'}`} title={tip}>
        {s.verdict === 'approved' ? '✓ Aprovada' : '✗ Reprovada'} ({s.deltaPerc >= 0 ? '+' : ''}{s.deltaPerc}pp)
    </span>;
}

/**
 * Reavaliações por IA (Fatia 2): barra COMPACTA no topo da tela de Estratégias (resumo + toggle +
 * "Ver detalhes") que abre um MODAL com as propostas do job semanal. Human-in-the-loop — aplica
 * via o fluxo existente (applyEvaluation) e marca o desfecho. props: onApplied (refresh), setNotification.
 */
function AiEvaluations({ onApplied, setNotification }) {
    const [evaluations, setEvaluations] = useState([]);
    const [autoOn, setAutoOn] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [expandedId, setExpandedId] = useState(null);
    const [selected, setSelected] = useState({}); // { [evalId]: { [suggestionId]: bool } }
    const [busyId, setBusyId] = useState(null);
    const [validation, setValidation] = useState({}); // { [evalId]: { loading, verdict, ... } }
    const [scoring, setScoring] = useState({}); // { [evalId]: { loading, verdict, original, modified, ... } }
    const [market, setMarket] = useState(getCurrentMarket());
    const [running, setRunning] = useState(false); // "Avaliar agora" em andamento

    useEffect(() => subscribeToCurrentMarket(setMarket), []);

    // Filtra as propostas pelo mercado atual (não misturar futuros na tela de spot).
    function loadEvaluations() {
        return getEvaluations('pending', market)
            .then(list => setEvaluations(Array.isArray(list) ? list : []))
            .catch(() => {})
            .finally(() => setLoaded(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { loadEvaluations(); }, [market]);

    useEffect(() => {
        getSettings()
            .then(s => setAutoOn(s && s.weeklyEvaluationEnabled === true))
            .catch(() => {});
    }, []);

    function notify(type, text) { if (setNotification) setNotification({ type, text }); }

    // "Avaliar agora": dispara a reavaliação por IA sob demanda e recarrega as propostas.
    function onRunNow() {
        setRunning(true);
        runEvaluationsNow()
            .then(r => {
                const n = (r && r.generated) || 0;
                notify(n > 0 ? 'success' : 'secondary',
                    n > 0 ? `${n} proposta(s) gerada(s). Veja em "Ver detalhes".`
                          : 'Nenhuma proposta gerada (sem estratégia ativa ou provedor de IA).');
                return loadEvaluations();
            })
            .catch(err => notify('danger', processError(err)))
            .finally(() => setRunning(false));
    }

    function onToggleAuto(e) {
        const on = e.target.checked;
        setAutoOn(on);
        updateSettings({ weeklyEvaluationEnabled: on })
            .then(() => notify('success', on
                ? 'Reavaliação semanal por IA LIGADA (roda aos domingos).'
                : 'Reavaliação semanal por IA desligada.'))
            .catch(err => { setAutoOn(!on); notify('danger', processError(err)); });
    }

    function onExpand(ev) {
        if (expandedId === ev.id) { setExpandedId(null); return; }
        setExpandedId(ev.id);
        const sel = {};
        ((ev.proposal && ev.proposal.suggestions) || []).forEach(s => { if (isApplicable(s)) sel[s.id] = true; });
        setSelected(prev => ({ ...prev, [ev.id]: sel }));
    }

    function toggleSuggestion(evId, sId) {
        setSelected(prev => ({ ...prev, [evId]: { ...(prev[evId] || {}), [sId]: !(prev[evId] || {})[sId] } }));
    }

    async function onApply(ev) {
        const sel = selected[ev.id] || {};
        const toApply = ((ev.proposal && ev.proposal.suggestions) || [])
            .filter(s => sel[s.id] && isApplicable(s))
            .map(s => ({ id: s.id, targetType: s.targetType, targetId: s.targetId, field: s.field, suggestedValue: s.suggestedValue }));
        if (!toApply.length) { notify('danger', 'Selecione ao menos uma sugestão aplicável.'); return; }
        setBusyId(ev.id);
        try {
            await applyEvaluation(ev.strategyId, toApply);
            await setEvaluationStatus(ev.id, 'applied');
            setEvaluations(list => list.filter(e => e.id !== ev.id));
            notify('success', `${toApply.length} sugestão(ões) aplicada(s) em ${ev.strategyName || 'estratégia'}. Snapshot de versão salvo (reversível).`);
            if (onApplied) onApplied();
        } catch (err) {
            notify('danger', processError(err));
        } finally {
            setBusyId(null);
        }
    }

    // Aplica o DIFF DE CONFIG (knobs do template, ex.: stop/lucro mínimo) via Reatualizar (P6.3).
    // Sucesso ou obsoleta (config já andou desde a geração — ex.: auto-tuner rodou no domingo):
    // a proposta sai da lista de pendentes nos dois casos (o backend já decidiu o desfecho).
    async function onApplyConfig(ev) {
        setBusyId(ev.id);
        try {
            const r = await applyEvaluationConfig(ev.id);
            if (r && r.obsolete) {
                notify('secondary', r.message || 'Proposta ficou obsoleta (a régua já mudou) — descartada sem aplicar.');
            } else {
                const desc = (r && r.description) || [];
                notify('success', `Régua ajustada em ${ev.strategyName || 'estratégia'}${desc.length ? `: ${desc.join(' · ')}` : ''}. Ponto de restauração criado.`);
            }
            setEvaluations(list => list.filter(e => e.id !== ev.id));
            if (onApplied) onApplied();
        } catch (err) {
            notify('danger', processError(err));
        } finally {
            setBusyId(null);
        }
    }

    // Camada 1: valida o conjunto selecionado no simulador (Consistente/Inconsistente) sem aplicar.
    async function onValidate(ev) {
        const sel = selected[ev.id] || {};
        const toValidate = ((ev.proposal && ev.proposal.suggestions) || [])
            .filter(s => sel[s.id] && isApplicable(s))
            .map(s => ({ id: s.id, targetType: s.targetType, targetId: s.targetId, field: s.field, suggestedValue: s.suggestedValue }));
        if (!toValidate.length) { notify('danger', 'Selecione ao menos uma sugestão aplicável.'); return; }
        setValidation(v => ({ ...v, [ev.id]: { loading: true } }));
        try {
            const r = await validateEvaluation(ev.strategyId, toValidate);
            setValidation(v => ({ ...v, [ev.id]: { loading: false, ...r } }));
        } catch (err) {
            setValidation(v => ({ ...v, [ev.id]: { loading: false, error: processError(err) } }));
        }
    }

    // Camada 2: backtest A/B (Aprovada/Reprovada) do conjunto selecionado. Dispara e faz poll.
    async function onScore(ev) {
        const sel = selected[ev.id] || {};
        const toScore = ((ev.proposal && ev.proposal.suggestions) || [])
            .filter(s => sel[s.id] && isApplicable(s))
            .map(s => ({ id: s.id, targetType: s.targetType, targetId: s.targetId, field: s.field, suggestedValue: s.suggestedValue }));
        if (!toScore.length) { notify('danger', 'Selecione ao menos uma sugestão aplicável.'); return; }
        setScoring(s => ({ ...s, [ev.id]: { loading: true } }));
        try {
            await scoreEvaluation(ev.strategyId, toScore, 30);
            for (let i = 0; i < 60; i++) { // ~3min de teto (backtest é pesado)
                await new Promise(r => setTimeout(r, 3000));
                const d = await getEvaluationScore(ev.strategyId);
                if (d && d.state === 'done') { setScoring(s => ({ ...s, [ev.id]: { loading: false, ...d.result } })); return; }
                if (d && d.state === 'error') { setScoring(s => ({ ...s, [ev.id]: { loading: false, error: d.error } })); return; }
            }
            setScoring(s => ({ ...s, [ev.id]: { loading: false, error: 'Tempo esgotado aguardando o backtest.' } }));
        } catch (err) {
            setScoring(s => ({ ...s, [ev.id]: { loading: false, error: processError(err) } }));
        }
    }

    async function onDismissAll() {
        if (!evaluations.length) return;
        const ok = await confirmDialog({
            title: 'Descartar propostas',
            message: `Descartar todas as ${evaluations.length} proposta(s) pendente(s) deste mercado?`,
            confirmLabel: 'Descartar todas',
            confirmVariant: 'danger'
        });
        if (!ok) return;
        try {
            const r = await dismissAllEvaluations(market);
            setEvaluations([]);
            notify('success', `${(r && r.dismissed) || 0} proposta(s) descartada(s).`);
        } catch (err) {
            notify('danger', processError(err));
        }
    }

    async function onDismiss(ev) {
        setBusyId(ev.id);
        try {
            await setEvaluationStatus(ev.id, 'dismissed');
            setEvaluations(list => list.filter(e => e.id !== ev.id));
            notify('success', 'Proposta descartada.');
        } catch (err) {
            notify('danger', processError(err));
        } finally {
            setBusyId(null);
        }
    }

    if (!loaded) return null;

    const pending = evaluations.length;

    return (
        <>
            {/* Barra compacta (1 linha) — destaca quando há propostas pendentes */}
            <div className={`card border-0 shadow-sm mb-3 ${pending > 0 ? 'border-start border-4 border-primary' : ''}`}>
                <div className="card-body py-2 d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div className="d-flex align-items-center gap-2">
                        <span style={{ fontSize: 18 }}>🧠</span>
                        <span className="fw-semibold">Reavaliações por IA</span>
                        {pending > 0
                            ? <span className="badge bg-primary">{pending} pendente(s)</span>
                            : <span className="text-muted small">nenhuma proposta pendente</span>}
                        {pending > 0 && (
                            <button className="btn btn-sm btn-primary py-0 ms-1" onClick={() => setShowModal(true)}>
                                Ver detalhes
                            </button>
                        )}
                        <button className="btn btn-sm btn-outline-primary py-0 ms-1" onClick={onRunNow} disabled={running}
                            title="Reavalia agora todas as estratégias ativas (não espera o domingo).">
                            {running ? 'Avaliando…' : '⚡ Avaliar agora'}
                        </button>
                    </div>
                    <div className="form-check form-switch mb-0" title="Roda aos domingos, propondo ajustes para você revisar. Nada é aplicado sozinho.">
                        <input className="form-check-input" type="checkbox" id="weeklyEvalToggle"
                            checked={autoOn} onChange={onToggleAuto} />
                        <label className="form-check-label small" htmlFor="weeklyEvalToggle">
                            Avaliar automaticamente toda semana
                        </label>
                    </div>
                </div>
            </div>

            {/* Modal com a lista de propostas (detalhe) */}
            {showModal && (
                <>
                    <div className="modal-backdrop fade show" onClick={() => setShowModal(false)}></div>
                    <div className="modal fade show d-block" tabIndex="-1" role="dialog">
                        <div className="modal-dialog modal-lg modal-dialog-scrollable modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">🧠 Reavaliações por IA</h5>
                                    <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
                                </div>
                                <div className="modal-body">
                                    <p className="text-muted small">
                                        A IA analisa o desempenho recente de cada estratégia ativa + a validação offline e
                                        propõe ajustes. Nada é aplicado sozinho — você revisa e decide.
                                    </p>

                                    {pending === 0 && (
                                        <p className="text-muted small fst-italic mb-0">Nenhuma proposta pendente no momento.</p>
                                    )}

                                    {evaluations.map(ev => {
                                        const suggestions = (ev.proposal && ev.proposal.suggestions) || [];
                                        const sel = selected[ev.id] || {};
                                        const selCount = suggestions.filter(s => sel[s.id] && isApplicable(s)).length;
                                        const open = expandedId === ev.id;
                                        return (
                                            <div key={ev.id} className="border rounded p-2 mb-3">
                                                <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                                                    <div className="flex-grow-1">
                                                        <strong>{ev.strategyName || `Estratégia #${ev.strategyId}`}</strong>
                                                        <span className="text-muted small ms-2" title="Quando a IA gerou esta avaliação">
                                                            {ev.createdAt ? new Date(ev.createdAt).toLocaleString() : ''}
                                                        </span>
                                                        {ev.proposal && ev.proposal.summary && (
                                                            <p className="mb-0 small mt-1">{ev.proposal.summary}</p>
                                                        )}
                                                        {ev.proposal && ev.proposal.configDiff && ev.proposal.configDiff.changes && ev.proposal.configDiff.changes.length > 0 && (
                                                            <span className="badge bg-info text-dark mt-1">⚙️ Ajuste de régua proposto</span>
                                                        )}
                                                    </div>
                                                    <div className="d-flex gap-1 flex-shrink-0">
                                                        <button className="btn btn-sm btn-outline-primary py-0" onClick={() => onExpand(ev)}>
                                                            {open ? 'Fechar' : `Revisar (${suggestions.length})`}
                                                        </button>
                                                        <button className="btn btn-sm btn-outline-secondary py-0"
                                                            disabled={busyId === ev.id} onClick={() => onDismiss(ev)}>
                                                            Descartar
                                                        </button>
                                                    </div>
                                                </div>

                                                {open && (
                                                    <div className="mt-2">
                                                        {ev.proposal && ev.proposal.configDiff && ev.proposal.configDiff.changes && ev.proposal.configDiff.changes.length > 0 && (
                                                            <div className="card mb-2 border-info">
                                                                <div className="card-body py-2">
                                                                    <div className="small fw-semibold mb-1">⚙️ Ajuste de régua proposto</div>
                                                                    <ul className="small mb-2 ps-3">
                                                                        {ev.proposal.configDiff.changes.map(c => (
                                                                            <li key={c.key}>
                                                                                {c.label}: <span className="text-muted text-decoration-line-through">{c.from != null ? String(c.from) : '—'}</span>
                                                                                {' → '}
                                                                                <span className="text-success fw-semibold">{String(c.to)}</span>
                                                                                {c.clamped && <span className="text-muted"> (ajustado ao limite)</span>}
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                    <button className="btn btn-sm btn-success" disabled={busyId === ev.id}
                                                                        onClick={() => onApplyConfig(ev)}>
                                                                        {busyId === ev.id ? 'Aplicando...' : 'Aplicar régua (com teste e restauração)'}
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        )}
                                                        {suggestions.length === 0 && (
                                                            <p className="text-muted small mb-0">Sem sugestões nesta proposta.</p>
                                                        )}
                                                        {suggestions.map(s => {
                                                            const applicable = isApplicable(s);
                                                            const condInvalid = isConditionsInvalid(s);
                                                            return (
                                                                <div key={s.id} className={`card mb-2 border-${IMPACT_COLOR[s.impact] || 'secondary'}`}>
                                                                    <div className="card-body py-2">
                                                                        <div className="d-flex gap-2">
                                                                            {applicable && (
                                                                                <input type="checkbox" className="form-check-input mt-1 flex-shrink-0"
                                                                                    checked={!!sel[s.id]} onChange={() => toggleSuggestion(ev.id, s.id)} />
                                                                            )}
                                                                            <div className="flex-grow-1">
                                                                                <div className="d-flex justify-content-between">
                                                                                    <span className="small fw-semibold">
                                                                                        {s.targetType ? `${s.targetType}` : ''} {s.field && s.field !== 'description_only' ? `· ${s.field}` : ''}
                                                                                    </span>
                                                                                    {s.field === 'description_only'
                                                                                        ? <span className="badge bg-light text-dark border">Manual</span>
                                                                                        : <span className={`badge bg-${IMPACT_COLOR[s.impact] || 'secondary'}`}>{s.impact || 'info'}</span>}
                                                                                </div>
                                                                                {s.description && <p className="mb-1 small fw-semibold">{s.description}</p>}
                                                                                {s.reasoning && <p className="mb-1 text-muted small">{s.reasoning}</p>}
                                                                                {applicable && (
                                                                                    <div className="small">
                                                                                        {s.currentValue != null && (
                                                                                            <div className="text-muted text-decoration-line-through" style={{ wordBreak: 'break-all' }}>{String(s.currentValue)}</div>
                                                                                        )}
                                                                                        <div className="text-success" style={{ wordBreak: 'break-all' }}>{String(s.suggestedValue)}</div>
                                                                                    </div>
                                                                                )}
                                                                                {condInvalid && (
                                                                                    <p className="mb-0 small text-danger">Sugestão de condição inválida — não aplicável automaticamente.</p>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                        <div className="d-flex gap-2 align-items-center flex-wrap">
                                                            <button className="btn btn-sm btn-outline-primary"
                                                                disabled={selCount === 0 || (validation[ev.id] && validation[ev.id].loading)}
                                                                onClick={() => onValidate(ev)}
                                                                title="Roda as sugestões selecionadas no simulador e diz se são consistentes (não quebram a estratégia).">
                                                                {validation[ev.id] && validation[ev.id].loading ? 'Validando...' : '🧪 Validar no simulador'}
                                                            </button>
                                                            <ValidationBadge v={validation[ev.id]} />
                                                            <button className="btn btn-sm btn-outline-info"
                                                                disabled={selCount === 0 || (scoring[ev.id] && scoring[ev.id].loading)}
                                                                onClick={() => onScore(ev)}
                                                                title="Backtest A/B em candles reais: compara a estratégia atual com a versão sugerida (lucro/drawdown). Pesado — pode levar 1-3 min.">
                                                                {scoring[ev.id] && scoring[ev.id].loading ? 'Testando...' : '📊 Testar (backtest A/B)'}
                                                            </button>
                                                            <ScoreBadge s={scoring[ev.id]} />
                                                            <button className="btn btn-sm btn-success" disabled={busyId === ev.id || selCount === 0}
                                                                onClick={() => onApply(ev)}>
                                                                {busyId === ev.id ? 'Aplicando...' : `Aplicar selecionadas (${selCount})`}
                                                            </button>
                                                        </div>

                                                        {/* Backtest A/B: score da estratégia ATUAL e da SUGERIDA lado a lado */}
                                                        {scoring[ev.id] && !scoring[ev.id].loading && !scoring[ev.id].error && scoring[ev.id].verdict !== 'na' && (
                                                            <div className="small mt-2 p-2 rounded bg-light border">
                                                                <div className="fw-semibold mb-1">
                                                                    📊 Backtest A/B — {scoring[ev.id].days}d · {scoring[ev.id].symbol}
                                                                    <span className={`badge ms-2 bg-${scoring[ev.id].verdict === 'approved' ? 'success' : 'danger'}`}>
                                                                        {scoring[ev.id].verdict === 'approved' ? 'Aprovada' : 'Reprovada'}
                                                                    </span>
                                                                </div>
                                                                <table className="table table-sm mb-0" style={{ fontSize: '0.8rem' }}>
                                                                    <thead><tr><th></th><th>Atual</th><th>Sugerida</th></tr></thead>
                                                                    <tbody>
                                                                        <tr>
                                                                            <td className="text-muted">Lucro</td>
                                                                            <td>{scoring[ev.id].original.profitPerc}%</td>
                                                                            <td className={scoring[ev.id].deltaPerc >= 0 ? 'text-success fw-semibold' : 'text-danger fw-semibold'}>
                                                                                {scoring[ev.id].modified.profitPerc}% ({scoring[ev.id].deltaPerc >= 0 ? '+' : ''}{scoring[ev.id].deltaPerc}pp)
                                                                            </td>
                                                                        </tr>
                                                                        <tr><td className="text-muted">Drawdown</td><td>{scoring[ev.id].original.maxDrawdown}%</td><td>{scoring[ev.id].modified.maxDrawdown}%</td></tr>
                                                                        <tr><td className="text-muted">Win rate</td><td>{scoring[ev.id].original.winRate}%</td><td>{scoring[ev.id].modified.winRate}%</td></tr>
                                                                        <tr><td className="text-muted">Operações</td><td>{scoring[ev.id].original.operations}</td><td>{scoring[ev.id].modified.operations}</td></tr>
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className="modal-footer justify-content-between">
                                    {pending > 0
                                        ? <button type="button" className="btn btn-outline-danger btn-sm" onClick={onDismissAll}>Descartar todas ({pending})</button>
                                        : <span />}
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Fechar</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
}

export default AiEvaluations;
